/**
 * Everything on a customer's Settings page below the customer frame: one page
 * with no sub-tabs (docs/features/credit-control/06_ui_strategy.md §2,
 * decision 10), whose UI credit control owns:
 * - the billing account strip: whose account, the Sage account, the Xero
 *   contact, and how many customers it pays for;
 * - "Billed to": who pays for this customer's orders, changed with its own
 *   dialog and reason (spec 20), not the save bar;
 * - "Payment methods": how the payer may pay, with the sales ledger's
 *   "Invoicing on credit account" nested in the credit account block;
 * - "Stock billing", the sales ledger's, for every payer;
 * - "Period charges", the sales ledger's (V1 completion G5): what the payer is
 *   billed on a schedule with no order. It acts through commands that commit
 *   at once, so it sits outside the drafts and the save bar.
 * The sales ledger's parts show while the read carries them (the
 * `sales_ledger` feature is on for this user). A sub-account has the strip
 * and its "Billed to" row alone; Pollards' internal account (own stock, never
 * billed) has the strip alone, since nobody edits it and it is in no billing
 * group: no Billed to, payment methods, stock billing or Save.
 *
 * Edits live here, in the page's drafts, not in the cards. One save bar at the
 * bottom appears while anything is edited; its one Save writes to each owner
 * in turn (`useSaveSettings`). Leaving with edits is guarded once, from the
 * same drafts. Everyone but finance, managers and admins reads the page, with
 * one note saying so.
 *
 * Self-loading by customer id.
 */
import type { ReactNode } from "react";
import type { CustomerCreditView } from "@shared-core/data/credit-control-types";
import {
  describeInvoicingChanges,
  describePaymentChanges,
} from "@shared-core/domain/settings-change-summary";
import { Alert, AlertDescription } from "../../primitives/alert";
import { Skeleton } from "../../primitives/skeleton";
import { UnsavedChangesGuard } from "../../patterns/UnsavedChangesGuard";
import { BilledToRow } from "./BilledToRow";
import { BillingAccountHeader } from "./BillingAccountHeader";
import {
  CreditAccountInvoicing,
  StockBillingSection,
} from "./InvoicingSettingsSections";
import { PaymentSettingsSection } from "./PaymentSettingsSection";
import { PeriodChargesSection } from "./PeriodChargesSection";
import { SettingsSaveBar } from "./SettingsSaveBar";
import { useCustomerSettings } from "./useCustomerSettings";
import {
  INVOICING_PART,
  PAYMENT_PART,
  useSaveSettings,
} from "./useSaveSettings";
import { useInvoicingDraft, usePaymentDraft } from "./useSettingsDrafts";

export function CustomerSettingsBody({ customerId }: { customerId: string }) {
  const { credit, reloadCredit } = useCustomerSettings(customerId);

  if (credit.kind === "failed") {
    return <Failure>Could not load payment settings: {credit.message}</Failure>;
  }
  if (credit.kind === "loading") {
    return (
      <div
        role="status"
        aria-label="Loading settings"
        className="space-y-4 rounded-lg border border-border p-4"
      >
        <Skeleton className="h-4 w-2/3" />
        <Skeleton className="h-4 w-1/2" />
      </div>
    );
  }

  return (
    <LoadedSettings
      key={customerId}
      credit={credit.value}
      reloadCredit={reloadCredit}
    />
  );
}

function LoadedSettings({
  credit,
  reloadCredit,
}: {
  credit: CustomerCreditView;
  reloadCredit: () => Promise<void>;
}) {
  const payment = usePaymentDraft(credit);
  const invoicing = useInvoicingDraft(credit);
  const {
    saving,
    paymentError,
    invoicingError,
    barMessage,
    save,
    clearMessages,
  } = useSaveSettings({ view: credit, payment, invoicing, reloadCredit });

  const isInternal =
    credit.payer === null && credit.billingAccount?.kind === "internal";
  const isReadOnly = credit.payer !== null || isInternal;
  const dirtyParts = [
    ...(payment.dirty ? [PAYMENT_PART] : []),
    ...(invoicing.dirty ? [INVOICING_PART] : []),
  ];
  const summary = [
    ...(payment.dirty
      ? describePaymentChanges(payment.saved, payment.values, {
          methods: credit.paymentMethodOptions,
          termsBasis: credit.termsBasisOptions,
          poRule: credit.poRuleOptions,
        })
      : []),
    ...(invoicing.dirty && invoicing.invoicing
      ? describeInvoicingChanges(invoicing.invoicing, invoicing.changes)
      : []),
  ];

  const reset = () => {
    payment.reset();
    invoicing.reset();
    clearMessages();
  };

  return (
    <div className="space-y-4">
      <UnsavedChangesGuard when={dirtyParts.length > 0} />
      <BillingAccountHeader credit={credit} />
      {!isInternal && <BilledToRow credit={credit} onChanged={reloadCredit} />}
      {!isReadOnly && (
        <>
          <PaymentSettingsSection
            view={credit}
            payment={payment}
            saving={saving}
            error={paymentError}
            onChanged={reloadCredit}
            creditAccountInvoicing={
              <CreditAccountInvoicing
                draft={invoicing}
                saving={saving}
                error={invoicingError}
              />
            }
          />
          <StockBillingSection
            draft={invoicing}
            saving={saving}
            error={invoicingError}
          />
          {credit.invoicing && credit.billingAccount && (
            <PeriodChargesSection
              billingAccountRef={credit.billingAccount.ref}
            />
          )}
          {credit.canManage ? (
            <SettingsSaveBar
              customerName={credit.customerName}
              dirtyParts={dirtyParts}
              summary={summary}
              blockedReason={
                payment.dirty && payment.invalid
                  ? "Fix the payment settings marked above to save."
                  : null
              }
              saving={saving}
              message={barMessage}
              onReset={reset}
              onConfirm={(reason) => void save(reason)}
            />
          ) : (
            <p className="text-xs text-muted-foreground">
              Finance, managers and admins change these.
            </p>
          )}
        </>
      )}
    </div>
  );
}

function Failure({ children }: { children: ReactNode }) {
  return (
    <Alert variant="destructive" role="alert">
      <AlertDescription>{children}</AlertDescription>
    </Alert>
  );
}
